import { db } from "../db/database";
import type { AppSettings, AuditLog, ImportBatch, InventoryBalance, InventoryMovement, MarketplaceListing, MarketplaceVariantListing, PendingSync, Product, ProductImportBatch, ProductVariant, VariantAlias } from "../types";
import { apiRequest } from "./api";
import { ensureSyncSession } from "./auth";

interface PushResponse {
  results: Array<{ id: string; status: "sincronizado" | "conflito" | "erro"; message?: string }>;
  cursor?: string;
}

interface PullResponse {
  cursor: string;
  products?: Product[];
  variants?: ProductVariant[];
  balances?: InventoryBalance[];
  movements?: InventoryMovement[];
  imports?: ImportBatch[];
  variantAliases?: VariantAlias[];
  marketplaceListings?: MarketplaceListing[];
  marketplaceVariants?: MarketplaceVariantListing[];
  productImports?: ProductImportBatch[];
  auditLogs?: AuditLog[];
  settings?: Partial<AppSettings>;
}

const AUTO_SYNC_EVENT = "wg-tricot:auto-sync";
const AUTO_SYNC_CHANNEL = "wg-tricot-sync";
const AUTO_SYNC_INTERVAL_MS = 12_000;
let activeSync: Promise<{ pushed: number; conflicts: number }> | undefined;

async function performSync(): Promise<{ pushed: number; conflicts: number }> {
  if (!navigator.onLine) return { pushed: 0, conflicts: 0 };
  const settings = await db.settings.get("app");
  if (!settings?.apiUrl) throw new Error("A API do servidor não foi localizada.");
  const session = await ensureSyncSession();
  if (!session?.accessToken) throw new Error("A sessão ainda não está autorizada para sincronizar.");
  let pushed = 0;
  let conflicts = 0;

  const pending = await db.pendingSync.where("status").anyOf("pendente", "erro").sortBy("createdAt");
  if (pending.length) {
    const attemptAt = new Date().toISOString();
    await db.pendingSync.bulkPut(pending.map((item) => ({ ...item, status: "sincronizando", attempts: item.attempts + 1, updatedAt: attemptAt })));
    let response: PushResponse;
    try {
      response = await apiRequest<PushResponse>("/sync/push", { method: "POST", body: JSON.stringify({ operations: pending }) });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Falha ao sincronizar.";
      await db.pendingSync.bulkPut(pending.map((item) => ({ ...item, status: "erro", attempts: item.attempts + 1, lastError: message, updatedAt: new Date().toISOString() })));
      throw error;
    }
    const received = new Set(response.results.map((result) => result.id));
    for (const result of response.results) {
      const operation = await db.pendingSync.get(result.id);
      if (!operation) continue;
      const status: PendingSync["status"] = result.status;
      await db.pendingSync.update(result.id, { status, lastError: result.message, updatedAt: new Date().toISOString() });
      if (status === "sincronizado") {
        pushed += 1;
        await db.movements.where("operationId").equals(result.id).modify({ syncStatus: "sincronizado" });
      }
      if (status === "conflito") {
        conflicts += 1;
        await db.movements.where("operationId").equals(result.id).modify({ syncStatus: "conflito" });
      }
    }
    const missing = pending.filter((item) => !received.has(item.id));
    if (missing.length) {
      await db.pendingSync.bulkPut(missing.map((item) => ({ ...item, status: "erro", attempts: item.attempts + 1, lastError: "O servidor não confirmou esta operação.", updatedAt: new Date().toISOString() })));
    }
  }

  const cursor = (await db.kv.get("sync_cursor"))?.value;
  const query = typeof cursor === "string" ? `?cursor=${encodeURIComponent(cursor)}&t=${Date.now()}` : `?t=${Date.now()}`;
  const pull = await apiRequest<PullResponse>(`/sync/pull${query}`);
  const blockingOperations = await db.pendingSync.where("status").anyOf("pendente", "sincronizando", "erro").count();
  const replaceLocalSnapshot = blockingOperations === 0;
  await db.transaction("rw", [db.products, db.variants, db.balances, db.movements, db.imports, db.auditLogs, db.settings, db.kv, db.variantAliases, db.marketplaceListings, db.marketplaceVariants, db.productImports], async () => {
    const applySnapshot = async <T>(table: { clear: () => Promise<unknown>; bulkPut: (values: T[]) => Promise<unknown> }, values?: T[]) => {
      if (!values) return;
      if (replaceLocalSnapshot) await table.clear();
      if (values.length) await table.bulkPut(values);
    };
    await applySnapshot(db.products, pull.products);
    await applySnapshot(db.variants, pull.variants);
    await applySnapshot(db.balances, pull.balances);
    await applySnapshot(db.movements, pull.movements);
    await applySnapshot(db.imports, pull.imports);
    await applySnapshot(db.variantAliases, pull.variantAliases);
    await applySnapshot(db.marketplaceListings, pull.marketplaceListings);
    await applySnapshot(db.marketplaceVariants, pull.marketplaceVariants);
    await applySnapshot(db.productImports, pull.productImports);
    await applySnapshot(db.auditLogs, pull.auditLogs);
    if (pull.settings) await db.settings.update("app", { ...pull.settings, demoData: false });
    await db.kv.put({ key: "sync_cursor", value: pull.cursor });
    await db.settings.update("app", { lastSync: new Date().toISOString(), demoData: false });
  });
  return { pushed, conflicts };
}

export function syncNow(): Promise<{ pushed: number; conflicts: number }> {
  if (activeSync) return activeSync;
  activeSync = performSync().finally(() => { activeSync = undefined; });
  return activeSync;
}

export function requestAutoSync(): void {
  window.dispatchEvent(new Event(AUTO_SYNC_EVENT));
  if ("BroadcastChannel" in window) {
    const channel = new BroadcastChannel(AUTO_SYNC_CHANNEL);
    channel.postMessage("changed");
    channel.close();
  }
}

export function installAutoSync(onStatus?: (status: "sincronizando" | "concluido" | "erro") => void) {
  let disposed = false;
  let debounceTimer: number | undefined;
  const channel = "BroadcastChannel" in window ? new BroadcastChannel(AUTO_SYNC_CHANNEL) : undefined;
  const attempt = async () => {
    if (disposed || !navigator.onLine || document.visibilityState === "hidden") return;
    onStatus?.("sincronizando");
    try {
      await syncNow();
      onStatus?.("concluido");
    } catch {
      onStatus?.("erro");
    }
  };
  const schedule = (delay = 180) => {
    if (disposed) return;
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => void attempt(), delay);
  };
  const visible = () => { if (document.visibilityState === "visible") schedule(0); };
  const requested = () => schedule();
  window.addEventListener("online", requested);
  window.addEventListener("focus", requested);
  window.addEventListener("pageshow", requested);
  window.addEventListener(AUTO_SYNC_EVENT, requested);
  document.addEventListener("visibilitychange", visible);
  if (channel) channel.onmessage = requested;
  const timer = window.setInterval(() => schedule(0), AUTO_SYNC_INTERVAL_MS);
  schedule(0);
  return () => {
    disposed = true;
    window.removeEventListener("online", requested);
    window.removeEventListener("focus", requested);
    window.removeEventListener("pageshow", requested);
    window.removeEventListener(AUTO_SYNC_EVENT, requested);
    document.removeEventListener("visibilitychange", visible);
    if (debounceTimer) window.clearTimeout(debounceTimer);
    window.clearInterval(timer);
    channel?.close();
  };
}
